import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps, FC, ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { Spinner, type SpinnerProps } from './spinner';

const loadingVariants = cva('text-muted-foreground', {
  variants: {
    variant: {
      block: 'flex flex-col items-center justify-center gap-3 py-8',
      inline: 'inline-flex flex-row items-center gap-2',
    },
  },
  defaultVariants: {
    variant: 'block',
  },
});

type LoadingVariant = NonNullable<VariantProps<typeof loadingVariants>['variant']>;

const defaultSpinnerSize: Record<LoadingVariant, SpinnerProps['size']> = {
  block: 'md',
  inline: 'sm',
};

export interface LoadingProps
  extends Omit<ComponentProps<'div'>, 'children'>,
    VariantProps<typeof loadingVariants> {
  /** Text shown next to the spinner. Pass an empty string to render the spinner on its own. */
  label?: string;
  size?: SpinnerProps['size'];
  tone?: SpinnerProps['tone'];
  /** Extra classes forwarded to the spinner icon. */
  spinnerClassName?: string;
  /** Custom content rendered in place of `label`. */
  children?: ReactNode;
}

export const Loading: FC<LoadingProps> = ({
  variant,
  label = 'Loading...',
  size,
  tone = 'brand',
  className,
  spinnerClassName,
  children,
  ...props
}) => {
  const resolvedVariant: LoadingVariant = variant ?? 'block';
  const content = children ?? (label ? <span className="text-sm">{label}</span> : null);

  return (
    <div
      role="status"
      aria-live="polite"
      // A status region is not named by its descendant text, so name it explicitly. With custom
      // `children` the caller owns the content, so let that be announced instead of a stale label.
      aria-label={children ? undefined : label || 'Loading'}
      data-slot="loading"
      data-testid="loading"
      className={cn(loadingVariants({ variant: resolvedVariant }), className)}
      {...props}
    >
      {/* The wrapper already owns the live region; hide the icon so it is not announced twice. */}
      <Spinner
        size={size ?? defaultSpinnerSize[resolvedVariant]}
        tone={tone}
        aria-hidden
        className={spinnerClassName}
      />
      {content}
    </div>
  );
};
